import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type AppRole = "admin" | "doctor" | "receptionist" | "staff" | "viewer" | "platform_admin";

export function useUserRoles() {
  const { session } = useAuth();
  const userId = session?.user?.id;

  const query = useQuery({
    queryKey: ["user_roles", userId],
    enabled: !!userId,
    queryFn: async (): Promise<AppRole[]> => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId!);
      if (error) throw error;
      return (data ?? []).map((r: { role: AppRole }) => r.role);
    },
  });

  const roles = query.data ?? [];
  const hasRole = (r: AppRole) => roles.includes(r);
  const isAdmin = hasRole("admin");
  const isDoctor = hasRole("doctor");
  const isReceptionist = hasRole("receptionist");
  const isStaff = hasRole("staff");
  const isViewer = hasRole("viewer");
  const isPlatformAdmin = hasRole("platform_admin");
  // Owner (admin), doctors, receptionists and legacy staff can manage the queue.
  const canManageQueue = isAdmin || isDoctor || isReceptionist || isStaff;
  // Clinical (prescribing / patient records) access.
  const canPrescribe = isAdmin || isDoctor;

  return {
    ...query,
    roles,
    hasRole,
    isAdmin,
    isDoctor,
    isReceptionist,
    isStaff,
    isViewer,
    isPlatformAdmin,
    canManageQueue,
    canPrescribe,
  };
}
